import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Chip,
} from "@mui/material";
import { format } from "date-fns";
import { UnscheduledAPISession } from "../../../redux/reducers/scheduleReducer";
import { getSessionStatusColor } from "./utils";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";

interface Props {
  session: UnscheduledAPISession;
  onClick: () => void;
  isSelected: boolean;
}

const UnscheduledSessionCard: React.FC<Props> = ({
  session,
  onClick,
  isSelected,
}) => {
  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: "pointer",
        transition: "all 0.2s ease",
        backgroundColor: isSelected ? "#e3f2fd" : "#ffffff",
        border: isSelected ? "2px solid #2196f3" : "1px solid #e0e0e0",
        "&:hover": {
          boxShadow: 3,
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Stack spacing={1.5}>
          {/* Session ID and Status */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              {session.s.session_id.slice(0, 8)}...
            </Typography>
            <Chip
              label={session.s.status}
              size="small"
              sx={{
                backgroundColor: getSessionStatusColor(session.s.status),
                color: "#fff",
                fontWeight: 500,
                fontSize: "0.75rem",
              }}
            />
          </Box>

          {/* User Email */}
          {session.s.user_email && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography
                variant="body2"
                title={session.s.user_email}
                sx={{
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
              >
                {session.s.user_email}
              </Typography>
            </Box>
          )}

          {/* Created Time */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "0.75rem",
              }}
            >
              Created: {format(new Date(session.s.created_at), "MMM d, HH:mm")}
            </Typography>
          </Box>

          {/* Connection Method */}
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
            }}
          >
            Connection: {session.s.connection_method}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default UnscheduledSessionCard;
